"use client";

import type { AppKitNetwork } from "@reown/appkit/networks";
import { createAppKit, useAppKitTheme } from "@reown/appkit/react";
import React from "react";
import { WagmiProvider } from "wagmi";

import config from "configs/app";
import { currentChain } from "lib/web3/chains";
import wagmiConfig from "lib/web3/wagmiConfig";

const BODY_TYPEFACE = "Inter";
const feature = config.features.blockchainInteraction;

const init = () => {
  try {
    if (!feature.isEnabled || !wagmiConfig.adapter) {
      return;
    }

    createAppKit({
      adapters: [wagmiConfig.adapter],
      networks: [currentChain].filter(Boolean) as [
        AppKitNetwork,
        ...Array<AppKitNetwork>,
      ],
      metadata: {
        name: `${config.chain.name} explorer`,
        description: `${config.chain.name} explorer`,
        url: config.app.baseUrl,
        icons: [config.UI.navigation.icon.default],
      },
      projectId: feature.walletConnect.projectId,
      features: {
        analytics: false,
        email: false,
        socials: [],
        onramp: false,
        swaps: false,
      },
      themeVariables: {
        "--w3m-font-family": `${BODY_TYPEFACE}, sans-serif`,
        "--w3m-accent": "#2B6CB0",
        "--w3m-border-radius-master": "2px",
        "--w3m-z-index": 1500,
      },
      featuredWalletIds: [],
      allowUnsupportedChain: true,
    });
  } catch (error) {}
};

init();

interface Props {
  children: React.ReactNode;
}

const DefaultProvider = ({ children }: Props) => {
  return <WagmiProvider config={wagmiConfig.config}>{children}</WagmiProvider>;
};

const Web3ModalProvider = ({ children }: Props) => {
  const { setThemeMode } = useAppKitTheme();

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const theme = document.documentElement.getAttribute("data-theme") || "dark";
    setThemeMode(theme as "light" | "dark");

    const observer = new MutationObserver(() => {
      const current =
        document.documentElement.getAttribute("data-theme") || "dark";
      setThemeMode(current as "light" | "dark");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, [setThemeMode]);

  return <DefaultProvider>{children}</DefaultProvider>;
};

const Provider = feature.isEnabled ? Web3ModalProvider : DefaultProvider;

export default Provider;
